export type CredentialMode = 'BLOCK' | 'WARN'
export type CredentialContext = 'ASSIGNMENT' | 'TRANSFER' | 'MOBILISATION' | 'MANUAL'

/**
 * What failing a rule costs.
 *
 * Not a boolean on purpose: a site that legally cannot admit somebody without a valid pass
 * is a different thing from a site that would rather they had one.
 */
export const CREDENTIAL_MODE_LABEL: Record<CredentialMode, string> = {
  BLOCK: 'Blocks the placement',
  WARN: 'Warns, but lets it through',
}

export const CREDENTIAL_CONTEXT_LABEL: Record<CredentialContext, string> = {
  ASSIGNMENT: 'Assignment',
  TRANSFER: 'Transfer',
  MOBILISATION: 'Mobilisation',
  MANUAL: 'Recorded by hand',
}

export interface CredentialRuleView {
  id: string
  legalEntityId: string | null
  name: string
  description: string | null
  documentTypeId: string
  documentTypeName: string | null
  mode: CredentialMode
  nationality: string | null
  gradeId: string | null
  designationId: string | null
  locationId: string | null
  establishmentId: string | null
  expiredCountsAsMissing: boolean
  /** Fail a document that is still valid but runs out within this many days. */
  minRemainingDays: number | null
  effectiveFrom: string
  effectiveTo: string | null
  active: boolean
}

export interface CredentialFailureView {
  ruleId: string
  ruleName: string
  mode: CredentialMode
  documentTypeId: string
  documentTypeCode: string | null
  documentTypeName: string | null
  /** MISSING, EXPIRED or EXPIRING. */
  status: string
  expiryDate: string | null
  daysRemaining: number | null
  overridden: boolean
}

export interface CredentialCheckView {
  clear: boolean
  blocking: CredentialFailureView[]
  warnings: CredentialFailureView[]
}

export interface CredentialOverrideView {
  id: string
  employeeId: string
  credentialRuleId: string
  ruleName: string | null
  context: CredentialContext
  reason: string
  approvedBy: string
  approvedAt: string
  expiresAt: string | null
  revokedAt: string | null
  live: boolean
}

/** The shortest reason the server will accept — the database enforces the same floor. */
export const MIN_OVERRIDE_REASON = 10

export function failureSentence(failure: CredentialFailureView): string {
  switch (failure.status) {
    case 'MISSING':
      return 'not on file'
    case 'EXPIRED':
      return failure.expiryDate ? `expired on ${failure.expiryDate}` : 'expired'
    case 'EXPIRING':
      return failure.daysRemaining === null
        ? 'expiring too soon'
        : `expires in ${failure.daysRemaining} ${failure.daysRemaining === 1 ? 'day' : 'days'}`
    default:
      return failure.status.toLowerCase()
  }
}

// --- Phase 8 part 1: the statutory calendar, the ratio, filings and data-subject requests ---

export type ComplianceCategory = 'FILING' | 'RENEWAL' | 'PAYMENT' | 'RATIO' | 'AUDIT' | 'OTHER'
export type ComplianceFrequency =
  | 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'ANNUAL' | 'ON_EVENT' | 'ONE_OFF'
export type OccurrenceStatus =
  | 'OPEN' | 'IN_PROGRESS' | 'SUBMITTED' | 'DONE' | 'MISSED' | 'WAIVED'

export const COMPLIANCE_CATEGORY_LABEL: Record<ComplianceCategory, string> = {
  FILING: 'Filing',
  RENEWAL: 'Renewal',
  PAYMENT: 'Payment',
  RATIO: 'Ratio',
  AUDIT: 'Audit',
  OTHER: 'Other',
}

export const COMPLIANCE_FREQUENCY_LABEL: Record<ComplianceFrequency, string> = {
  MONTHLY: 'Every month',
  QUARTERLY: 'Every quarter',
  HALF_YEARLY: 'Twice a year',
  ANNUAL: 'Once a year',
  ON_EVENT: 'When something happens',
  ONE_OFF: 'Once',
}

export const OCCURRENCE_STATUS_LABEL: Record<OccurrenceStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  SUBMITTED: 'Submitted',
  DONE: 'Done',
  MISSED: 'Missed',
  WAIVED: 'Waived',
}

export const OCCURRENCE_STATUS_TONE: Record<OccurrenceStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  OPEN: 'neutral',
  IN_PROGRESS: 'info',
  SUBMITTED: 'info',
  DONE: 'success',
  MISSED: 'danger',
  WAIVED: 'warning',
}

export interface ComplianceItemView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  authority: string | null
  category: string
  frequency: string
  dueDayOfPeriod: number | null
  dueMonth: number | null
  leadDays: number | null
  sourceKind: string
  sourceRefId: string | null
  ownerEmployeeId: string | null
  ownerName: string | null
  instructions: string | null
  requiresEvidence: boolean
  active: boolean
  openPeriods: number
}

export interface OccurrenceView {
  id: string
  itemId: string
  itemCode: string | null
  itemName: string | null
  authority: string | null
  category: string | null
  legalEntityId: string | null
  periodLabel: string
  dueDate: string | null
  status: string
  ownerEmployeeId: string | null
  ownerName: string | null
  reference: string | null
  completedAt: string | null
  outcomeNote: string | null
  evidenceCount: number
  requiresEvidence: boolean
  daysUntilDue: number | null
  /** Set when there is no due date, so a blank cell is never read as "not due". */
  dueNote: string | null
}

export interface NationalisationView {
  legalEntityId: string | null
  year: number
  targetPercent: number | null
  basis: string
  countedNationalities: string[]
  headcount: number
  qualifying: number | null
  actualPercent: number | null
  gap: number | null
  /** Why there is no percentage, when there is none. */
  note: string | null
}

export interface WpsSubmissionView {
  id: string
  legalEntityId: string
  paymentBatchId: string | null
  periodStart: string
  periodEnd: string
  fileName: string | null
  recordCount: number | null
  totalAmount: number | null
  submittedAt: string | null
  channel: string | null
  reference: string | null
  status: string
  responseNote: string | null
}

export interface GpssaFilingView {
  id: string
  legalEntityId: string
  periodStart: string
  periodEnd: string
  employeeCount: number | null
  employerAmount: number | null
  employeeAmount: number | null
  totalAmount: number | null
  submittedAt: string | null
  reference: string | null
  status: string
  responseNote: string | null
}

export interface ComplianceSettingView {
  id: string | null
  legalEntityId: string | null
  dsrResponseDays: number | null
  reminderLeadDays: number | null
  allowErasureWhileEmployed: boolean
  notes: string | null
  /** Says what an unset window means, so the blank is never read as "immediately". */
  responseWindowNote: string | null
}

export interface DsrRequestView {
  id: string
  reference: string
  subjectPersonId: string
  subjectEmployeeId: string | null
  subjectName: string | null
  requestType: string
  receivedAt: string
  receivedVia: string | null
  dueAt: string | null
  status: string
  workflowInstanceId: string | null
  requestedNote: string | null
  decisionNote: string | null
  completedAt: string | null
  resultFileId: string | null
  dueNote: string | null
  actionsRecorded: number
}

export interface DsrActionView {
  id: string
  targetTable: string
  targetId: string | null
  fields: string[]
  action: string
  retainedReason: string | null
  createdAt: string
}

export interface ErasurePlanView {
  table: string
  fields: string[]
  note: string
}

export interface RetentionAnswer {
  documentId: string
  title: string
  keepUntil: string | null
  dueForReview: boolean
  note: string | null
}

export interface EvidenceView {
  id: string
  storedFileId: string
  note: string | null
  createdAt: string
}

export interface GenerateResult {
  year: number
  created: number
  note: string
}
